import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  PLATFORM_ID,
  QueryList,
  Renderer2,
  ViewEncapsulation,
  WritableSignal,
  computed,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { fromEvent, tap } from 'rxjs';
import { INPUT_FIELD, InputFieldDirective } from './input-field.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'form-field',
  standalone: true,
  imports: [InputFieldDirective],
  template: `<div class="form-field">
    <ng-content select="label[label]"></ng-content>
    <div class="form-field__wrapper">
      <div
        class="form-field__prefix"
        #prefix
        [class.form-field__suffix--empty]="emptyPrefix()"
      >
        <ng-content select="[formFieldPrefix]"></ng-content>
      </div>
      <div
        class="form-field__input"
        [class.form-field__input-with-clear-button]="allowClearButton()"
      >
        <ng-content select="label[floatingLabel]"></ng-content>
        <ng-content
          select="input[type=text][input-field], input[type=password][input-field], input[type=email][input-field], input[type=tel][input-field], input[type=url][input-field], input[type=number][input-field], input[type=range][input-field], input[type=date][input-field], input[type=month][input-field], input[type=week][input-field], input[type=time][input-field], input[type=datetime-local][input-field], input[type=color][input-field], input[type=file][input-field], div[inputSearch][input-field], select-field[input-field], password-field[input-field], phone-number-field[input-field], textarea[input-field], tags-selector[input-field]"
        ></ng-content>
        @if (allowClearButton()) {
          <button class="clear-button" type="button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
            >
              <g
                id="Groupe_19512"
                data-name="Groupe 19512"
                transform="translate(-952.057 -423.5)"
              >
                <rect
                  id="Rectangle_18338"
                  data-name="Rectangle 18338"
                  width="15"
                  height="15"
                  rx="7.5"
                  transform="translate(952.057 423.5)"
                  fill="rgba(69,69,69,0.1)"
                />
                <path
                  id="close"
                  d="M3.55,3.136,5.886.8a.389.389,0,0,0-.55-.55L3,2.586.664.25A.389.389,0,0,0,.114.8L2.45,3.136.114,5.473a.389.389,0,1,0,.55.55L3,3.686,5.337,6.022a.389.389,0,1,0,.55-.55Zm0,0"
                  transform="translate(956.557 428.364)"
                  fill="#454545"
                />
              </g>
            </svg>
          </button>
        }
      </div>
      <div
        class="form-field__suffix"
        #suffix
        [class.form-field__suffix--empty]="emptySuffix()"
      >
        <ng-content select="[formFieldSuffix]"></ng-content>
      </div>
    </div>
    <div
      class="form-field__feedback-messages-wrapper"
      #feedbackMessages
      [class.form-field__feedback-messages-wrapper--empty]="
        emptyFeedbackMessages()
      "
    >
      <ng-content select="[feedbackMessage]"></ng-content>
      <ng-content></ng-content>
    </div>
  </div>`,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormFieldComponent implements AfterContentInit, OnDestroy {
  private _renderer = inject(Renderer2);
  private _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private _destroyRef = inject(DestroyRef);
  private _platformId = inject(PLATFORM_ID);

  allowClearButton = input<boolean>(false);
  prefix = viewChild<ElementRef<HTMLDivElement>>('prefix');
  suffix = viewChild<ElementRef<HTMLDivElement>>('suffix');
  feedbackMessages = viewChild<ElementRef<HTMLDivElement>>('feedbackMessages');
  @Output() fieldCleared = new EventEmitter<void>();

  @ContentChildren(INPUT_FIELD, { descendants: true })
  inputFieldChildren!: QueryList<InputFieldDirective>;

  protected emptyPrefix = computed(() =>
    !this.prefix()?.nativeElement.hasChildNodes() ? true : null
  );

  protected emptySuffix = computed(() =>
    !this.suffix()?.nativeElement.hasChildNodes() ? true : null
  );

  protected emptyFeedbackMessages = computed(() =>
    !this.feedbackMessages()?.nativeElement.hasChildNodes() ? true : null
  );

  protected get _isPlatformBrowser(): boolean {
    return isPlatformBrowser(this._platformId);
  }

  private _observer: MutationObserver | undefined;
  private _currentInputField: WritableSignal<InputFieldDirective | undefined> =
    signal(undefined);

  ngAfterContentInit(): void {
    this._initializeInputFields();
    this._initializeLabels();
  }

  ngOnDestroy(): void {
    if (this._isPlatformBrowser) {
      this._observer?.disconnect();
    }
  }

  private _observeInputChanges(input: HTMLElement): void {
    const formField = this._elementRef.nativeElement.querySelector(
      '.form-field'
    ) as HTMLElement | null;

    this._observer = new MutationObserver(mutationList => {
      mutationList.forEach(mutation => {
        if (mutation.type == 'attributes') {
          const target = mutation.target as HTMLElement;
          if (mutation.attributeName === 'class') {
            target.classList.contains('input-focused')
              ? this._renderer.addClass(formField, 'form-field--focused')
              : this._renderer.removeClass(formField, 'form-field--focused');

            target.classList.contains('input-filled')
              ? this._renderer.addClass(formField, 'form-field--filled')
              : this._renderer.removeClass(formField, 'form-field--filled');

            target.classList.contains('input-disabled')
              ? this._renderer.addClass(formField, 'form-field--disabled')
              : this._renderer.removeClass(formField, 'form-field--disabled');

            target.classList.contains('input-readonly')
              ? this._renderer.addClass(formField, 'form-field--readonly')
              : this._renderer.removeClass(formField, 'form-field--readonly');

            if (
              target.classList.contains('ng-invalid') &&
              target.classList.contains('ng-dirty')
            ) {
              this._renderer.addClass(formField, 'form-field--error');
            } else {
              this._renderer.removeClass(formField, 'form-field--error');
            }
          }

          target.hasAttribute('disabled')
            ? this._renderer.addClass(formField, 'form-field--disabled')
            : this._renderer.removeClass(formField, 'form-field--disabled');

          target.hasAttribute('readonly')
            ? this._renderer.addClass(formField, 'form-field--readonly')
            : this._renderer.removeClass(formField, 'form-field--readonly');
        }
      });
    });

    this._observer.observe(input, { attributes: true });
  }

  private _handleHostInputFieldWrapperClick(
    inputFieldDirective: InputFieldDirective
  ): void {
    const input = inputFieldDirective.host as HTMLElement;
    const inputFielWrapper = input.closest('.form-field__wrapper');
    if (!inputFielWrapper) return;

    fromEvent(inputFielWrapper, 'click')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap(event => {
          inputFieldDirective.focus();
          const clearButton = (event.target as HTMLElement).closest(
            '.clear-button'
          );
          if (!clearButton) return;
          inputFieldDirective.clearInput();
          this.fieldCleared.emit();
        })
      )
      .subscribe();
  }

  private _initializeInputFields(): void {
    const inputs = this.inputFieldChildren.toArray();
    if (inputs.length) {
      const firstInputFieldDirective = inputs.shift()!;
      this._currentInputField.set(firstInputFieldDirective);
      const inputToDisplay = firstInputFieldDirective.host;

      if (inputs.length) {
        inputs.forEach(inputFieldDirective =>
          this._renderer.removeChild(
            inputFieldDirective.host.parentNode,
            inputFieldDirective.host
          )
        );
        throw new Error(
          "Multiple elements with the 'formField' attribute were detected! Please specify only one input."
        );
      }

      if (this._isPlatformBrowser) {
        this._observeInputChanges(inputToDisplay);
        this._handleHostInputFieldWrapperClick(firstInputFieldDirective);
      }
    }
  }

  private _initializeLabels(): void {
    const hostElement = this._elementRef.nativeElement.querySelector(
      '.form-field'
    ) as HTMLElement | null;
    const labelElements = Array.from(
      hostElement!.querySelectorAll('label[floatingLabel], label[label]')
    );

    if (labelElements.length) {
      const labelToDisplay = labelElements.shift() as HTMLLabelElement;
      this._renderer.addClass(labelToDisplay, 'form-field__label');
      if (labelToDisplay.hasAttribute('floatingLabel')) {
        this._renderer.addClass(labelToDisplay, 'form-field__label--floating');
        this._renderer.addClass(hostElement, 'form-field__floating-label');
      }

      if (this._isPlatformBrowser) {
        this._renderer.listen(labelToDisplay, 'click', () => {
          const labelForAttribute = labelToDisplay.getAttribute('for');
          const inputFieldId = this._currentInputField()?.host.getAttribute(
            'id'
          ) as string;

          if (labelForAttribute === inputFieldId) {
            this._currentInputField()?.focus();
          }
        });
      }

      if (labelElements.length) {
        labelElements.forEach(el =>
          this._renderer.removeChild(el.parentNode, el)
        );
        throw new Error(
          'Multiple labels were detected! Please specify only one label.'
        );
      }
    }
  }
}
